/**
 * The new-workflow gestation surface — a PURE layout shell (props in, JSX
 * out), sibling of `WorkflowDetail`. It hosts a workflow that does not
 * exist yet: a `specs` row is born only when the agent authors the spec
 * and the projection lands (10–30s after the praxis write), so creation
 * is THREAD-keyed — the `/workflows/new/$threadId` route mounts the real
 * `ChatWindow` (left) and the thread-keyed `SpecTab` (right, whose empty
 * state carries the SOP drop zone / paste input) and redirects to
 * `/workflows/$slug` the moment the spec row lands.
 *
 * Same two-pane frame + mobile Chat | Workflow toggle as `WorkflowDetail`;
 * all `dark:` variants preserved. No Convex hooks, no navigation — the
 * route wires those.
 */

import { useState, type ReactNode } from "react";

/** Which mobile pane is showing under `<lg` (desktop shows both). */
type MobilePane = "chat" | "workflow";

export interface WorkflowPendingProps {
  /** LEFT pane content — the route mounts the real `ChatWindow` here. */
  readonly chatSlot?: ReactNode;
  /** RIGHT pane body — the route embeds the thread-keyed `SpecTab` here. */
  readonly specSlot?: ReactNode;
  /** Fired by the "Back to workflows" affordance. Navigation lives in the route. */
  readonly onBack?: () => void;
}

export function WorkflowPending({
  chatSlot,
  specSlot,
  onBack,
}: WorkflowPendingProps) {
  const [mobilePane, setMobilePane] = useState<MobilePane>("workflow");

  return (
    <div className="mx-auto flex h-[calc(100vh-9rem)] min-h-[460px] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-home-ink/10 dark:border-white/10 bg-home-cream dark:bg-surface-dark-card max-lg:h-[calc(100dvh-11rem)] 2xl:h-[calc(100vh-4rem)] 2xl:max-w-none">
      {/* Mobile-only top-level Chat | Workflow toggle (hidden on `lg`+). */}
      <div className="flex items-center gap-2 border-b border-home-ink/8 dark:border-white/8 px-4 py-2.5 lg:hidden">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="mr-1 font-body text-[12px] font-semibold text-home-ink/55 dark:text-white/55"
          >
            ← Workflows
          </button>
        ) : null}
        <div className="ml-auto inline-flex gap-0.5 rounded-full bg-home-ink/[0.05] dark:bg-white/[0.06] p-0.5">
          {(["chat", "workflow"] as const).map((pane) => (
            <button
              key={pane}
              type="button"
              onClick={() => setMobilePane(pane)}
              aria-pressed={pane === mobilePane}
              className={`rounded-full px-3 py-1 font-body text-[12px] font-semibold capitalize transition-colors ${
                pane === mobilePane
                  ? "bg-white dark:bg-surface-dark-card text-home-ink dark:text-white shadow-[0_2px_8px_-4px_rgba(33,31,28,0.35)]"
                  : "text-home-ink/55 dark:text-white/55"
              }`}
            >
              {pane}
            </button>
          ))}
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Chat pane: fixed 40% on `lg`+, single-column when mobile toggle=chat. */}
        <div
          className={`flex-col border-home-ink/8 dark:border-white/8 bg-home-cream dark:bg-surface-dark-card lg:flex lg:w-[40%] lg:border-r ${
            mobilePane === "chat" ? "flex flex-1" : "hidden"
          }`}
        >
          {chatSlot ?? null}
        </div>

        {/* Workflow pane: fills the rest on `lg`+, single-column when toggle=workflow. */}
        <div
          className={`flex-1 flex-col lg:flex ${
            mobilePane === "workflow" ? "flex" : "hidden"
          }`}
        >
          <div className="flex h-full flex-col bg-home-cream-deep/40 dark:bg-surface-dark-raised">
            <div className="flex items-center justify-between gap-2 border-b border-home-ink/8 dark:border-white/8 px-5 py-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <h3 className="truncate font-display text-[15px] font-semibold text-home-ink dark:text-white">
                  New workflow
                </h3>
                <span className="shrink-0 rounded-full bg-home-ink/[0.05] dark:bg-white/[0.08] px-2.5 py-0.5 font-body text-[11px] font-semibold text-home-ink/55 dark:text-white/55">
                  Drafting
                </span>
              </div>
            </div>

            {/* Pane body — `relative` because the embedded SpecTab renders
                `absolute inset-0`. */}
            <div className="relative min-h-0 flex-1">{specSlot ?? null}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
